import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Modal,
  Table,
  Form,
  Input,
  Spin,
  Popconfirm,
  message,
  Switch,
  Tag,
  Tooltip,
  InputNumber,
  Space,
  Select,
  Button as AntButton,
} from 'antd';
import FeatherIcon from 'feather-icons-react';
import { PageHeader } from '../page-headers/page-headers';
import { Main, TableWrapper, BasicFormWrapper, AddUser } from '../../container/styled';
import { Button } from '../buttons/buttons';
import { Cards } from '../cards/frame/cards-frame';
import {
  fetchOptions,
  createOptionAction,
  updateOptionAction,
  deleteOptionAction,
  fetchSelections,
} from '../../redux/products/actionCreator';

// Mover SelectionsList fuera del componente para evitar re-creaciones
const SelectionsList = React.memo(
  ({
    existingSelections,
    newSelections,
    onUpdateSelection,
    onRemoveNewSelection,
    onRemoveExistingSelection,
    onAddSelection,
  }) => (
    <div>
      {existingSelections.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h4>Selecciones Existentes:</h4>
          {existingSelections.map((sel, idx) => (
            <div key={sel.id || idx} style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color="blue">
                {sel.title} {sel.extraPrice > 0 && `(+$${sel.extraPrice})`}
              </Tag>
              <AntButton
                type="link"
                danger
                size="small"
                icon={<FeatherIcon icon="x" size={14} />}
                onClick={() => onRemoveExistingSelection(sel.id)}
              />
            </div>
          ))}
        </div>
      )}

      <h4>Nuevas Selecciones:</h4>
      {newSelections.map(sel => (
        <Space key={sel.id} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
          <Input
            placeholder="Título (ej: Chico, Grande)"
            value={sel.title}
            onChange={e => onUpdateSelection(sel.id, 'title', e.target.value)}
            style={{ width: 200 }}
          />
          <InputNumber
            placeholder="Precio extra"
            value={sel.extraPrice}
            onChange={value => onUpdateSelection(sel.id, 'extraPrice', value || 0)}
            prefix="$"
            min={0}
            style={{ width: 120 }}
          />
          <AntButton
            type="link"
            danger
            icon={<FeatherIcon icon="trash-2" size={14} />}
            onClick={() => onRemoveNewSelection(sel.id)}
          />
        </Space>
      ))}
      <AntButton type="dashed" onClick={onAddSelection} block icon={<FeatherIcon icon="plus" size={14} />}>
        Agregar Selección
      </AntButton>
    </div>
  ),
);

SelectionsList.propTypes = {
  existingSelections: PropTypes.array.isRequired,
  newSelections: PropTypes.array.isRequired,
  onUpdateSelection: PropTypes.func.isRequired,
  onRemoveNewSelection: PropTypes.func.isRequired,
  onRemoveExistingSelection: PropTypes.func.isRequired,
  onAddSelection: PropTypes.func.isRequired,
};

const OptionsTable = ({ searchName = '' }) => {
  const dispatch = useDispatch();
  const { options = [], loadingOption } = useSelector(state => state.products);

  const [state, setState] = useState({
    modalVisible: false,
    modalUpdateVisible: false,
    idToUpdate: '',
    optionForm: {
      name: '',
      type: '',
      mandatory: false,
      selections: [],
    },
    newSelections: [],
  });

  const [optionForm] = Form.useForm();

  useEffect(() => {
    dispatch(fetchOptions({ searchName }));
    dispatch(fetchSelections());
  }, [dispatch, searchName]);

  const handleCancel = () => {
    setState({
      ...state,
      modalVisible: false,
      modalUpdateVisible: false,
      idToUpdate: '',
      optionForm: { name: '', type: '', mandatory: false, selections: [] },
      newSelections: [],
    });
    optionForm.resetFields();
  };

  const handleCreateOption = async values => {
    try {
      // Preparar todas las selections (existentes y nuevas)
      const allSelections = [
        ...state.optionForm.selections.map(sel => ({
          id: sel.id,
          title: sel.title,
          extraPrice: sel.extraPrice || 0,
        })),
        ...state.newSelections.map(sel => ({
          title: sel.title,
          extraPrice: sel.extraPrice || 0,
        })),
      ];

      // Enviar la opción con todas sus selections en una sola petición
      await dispatch(
        createOptionAction({
          name: values.name,
          type: values.type,
          mandatory: values.mandatory || false,
          selectionsData: allSelections,
        }),
      );

      handleCancel();
    } catch (error) {
      message.error('Error al crear la opción');
    }
  };

  const handleUpdateOption = async values => {
    try {
      // Preparar todas las selections (existentes y nuevas)
      const allSelections = [
        ...state.optionForm.selections.map(sel => ({
          id: sel.id,
          title: sel.title,
          extraPrice: sel.extraPrice || 0,
        })),
        ...state.newSelections.map(sel => ({
          title: sel.title,
          extraPrice: sel.extraPrice || 0,
        })),
      ];

      // Enviar la actualización con todas las selections
      await dispatch(
        updateOptionAction({
          id: state.idToUpdate,
          name: values.name,
          type: values.type,
          mandatory: values.mandatory || false,
          selectionsData: allSelections,
        }),
      );

      handleCancel();
    } catch (error) {
      message.error('Error al actualizar la opción');
    }
  };

  const handleDeleteOption = async id => {
    await dispatch(deleteOptionAction(id));
  };

  const toggleModal = () => {
    setState({ ...state, modalVisible: !state.modalVisible });
  };

  const handleShowUpdateModal = option => {
    setState({
      ...state,
      modalUpdateVisible: true,
      idToUpdate: option.id,
      optionForm: {
        name: option.name,
        type: option.type,
        mandatory: option.mandatory || false,
        selections: option.options || [],
      },
      newSelections: [],
    });
    optionForm.setFieldsValue({
      name: option.name,
      type: option.type,
      mandatory: option.mandatory || false,
    });
  };

  const addNewSelection = () => {
    setState(prevState => ({
      ...prevState,
      newSelections: [...prevState.newSelections, { id: Date.now() + Math.random(), title: '', extraPrice: 0 }],
    }));
  };

  const updateNewSelection = useCallback((id, field, value) => {
    setState(prevState => ({
      ...prevState,
      newSelections: prevState.newSelections.map(sel => (sel.id === id ? { ...sel, [field]: value } : sel)),
    }));
  }, []);

  const removeNewSelection = useCallback(id => {
    setState(prevState => ({
      ...prevState,
      newSelections: prevState.newSelections.filter(sel => sel.id !== id),
    }));
  }, []);

  const removeExistingSelection = useCallback(selectionId => {
    setState(prevState => ({
      ...prevState,
      optionForm: {
        ...prevState.optionForm,
        selections: prevState.optionForm.selections.filter(sel => sel.id !== selectionId),
      },
    }));
  }, []);

  const dataSource = options.map((option, index) => ({
    key: index + 1,
    id: option.id,
    name: <span className="option-name">{option.name}</span>,
    type: (
      <Tag color="#1890ff" style={{ fontWeight: 600, border: '1px solid #096dd9' }}>
        {option.type === 'BOOLEAN'
          ? 'Selección Única'
          : option.type === 'COUNTER'
          ? 'Selección Múltiple'
          : option.type || 'N/A'}
      </Tag>
    ),
    mandatory: (
      <span>
        {option.mandatory ? (
          <Tag color="#f5222d" style={{ fontWeight: 600, border: '1px solid #cf1322' }}>
            Obligatorio
          </Tag>
        ) : (
          <Tag color="#52c41a" style={{ fontWeight: 600, border: '1px solid #389e0d' }}>
            Opcional
          </Tag>
        )}
      </span>
    ),
    selections: (
      <Tooltip
        title={
          <div>
            {option.options?.map((sel, idx) => (
              <div key={idx}>
                {sel.title} {sel.extraPrice > 0 && `(+$${sel.extraPrice})`}
              </div>
            ))}
          </div>
        }
      >
        <Tag color="#722ed1" style={{ fontWeight: 600, border: '1px solid #531dab', cursor: 'pointer' }}>
          {option.options?.length || 0} selecciones
        </Tag>
      </Tooltip>
    ),
    action: (
      <div className="table-actions">
        <Button className="btn-icon" type="warning" onClick={() => handleShowUpdateModal(option)} shape="circle">
          <FeatherIcon icon="edit" size={16} />
        </Button>
        <Popconfirm
          title="¿Seguro que quieres borrar esta opción?"
          onConfirm={() => handleDeleteOption(option.id)}
          okText="Sí"
          cancelText="No"
        >
          <Button className="btn-icon" type="danger" shape="circle">
            <FeatherIcon icon="trash-2" size={16} />
          </Button>
        </Popconfirm>
      </div>
    ),
  }));

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Obligatorio',
      dataIndex: 'mandatory',
      key: 'mandatory',
    },
    {
      title: 'Selecciones',
      dataIndex: 'selections',
      key: 'selections',
    },
    {
      title: 'Acción',
      dataIndex: 'action',
      key: 'action',
    },
  ];

  return (
    <>
      <PageHeader
        ghost
        title="Opciones"
        buttons={[
          <div key="1" className="page-header-actions">
            <Button className="btn-add_new" size="default" type="primary" onClick={toggleModal}>
              <FeatherIcon icon="plus-square" size={14} /> Agregar Opción
            </Button>
          </div>,
        ]}
      />
      <Main>
        {/* Create Modal */}
        <Modal title="Nueva Opción" visible={state.modalVisible} footer={null} onCancel={handleCancel} width={700}>
          <BasicFormWrapper>
            <AddUser>
              <Form form={optionForm} layout="vertical" onFinish={handleCreateOption}>
                <Form.Item
                  label="Nombre de la Opción"
                  name="name"
                  rules={[{ required: true, message: 'Ingresa un nombre' }]}
                >
                  <Input placeholder="ej: Tamaño, Temperatura, Extras" />
                </Form.Item>

                <Form.Item label="Tipo" name="type" rules={[{ required: true, message: 'Selecciona un tipo' }]}>
                  <Select placeholder="Selecciona el tipo de opción">
                    <Select.Option value="BOOLEAN">Selección Única (solo una opción)</Select.Option>
                    <Select.Option value="COUNTER">Selección Múltiple (con cantidad)</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="¿Es obligatorio?" name="mandatory" valuePropName="checked">
                  <Switch style={{ display: 'flex', alignItems: 'center', height: '20px' }} />
                </Form.Item>

                <SelectionsList
                  existingSelections={state.optionForm.selections}
                  newSelections={state.newSelections}
                  onUpdateSelection={updateNewSelection}
                  onRemoveNewSelection={removeNewSelection}
                  onRemoveExistingSelection={removeExistingSelection}
                  onAddSelection={addNewSelection}
                />

                {loadingOption ? (
                  <Spin />
                ) : (
                  <Button htmlType="submit" size="default" type="primary" key="submit" style={{ marginTop: 16 }}>
                    Crear Opción
                  </Button>
                )}
              </Form>
            </AddUser>
          </BasicFormWrapper>
        </Modal>

        {/* Update Modal */}
        <Modal
          title="Actualizar Opción"
          visible={state.modalUpdateVisible}
          footer={null}
          onCancel={handleCancel}
          width={700}
        >
          <BasicFormWrapper>
            <AddUser>
              <Form form={optionForm} layout="vertical" onFinish={handleUpdateOption}>
                <Form.Item
                  label="Nombre de la Opción"
                  name="name"
                  rules={[{ required: true, message: 'Ingresa un nombre' }]}
                >
                  <Input placeholder="ej: Tamaño, Temperatura, Extras" />
                </Form.Item>

                <Form.Item label="Tipo" name="type" rules={[{ required: true, message: 'Selecciona un tipo' }]}>
                  <Select placeholder="Selecciona el tipo de opción">
                    <Select.Option value="BOOLEAN">Selección Única (solo una opción)</Select.Option>
                    <Select.Option value="COUNTER">Selección Múltiple (con cantidad)</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item label="¿Es obligatorio?" name="mandatory" valuePropName="checked">
                  <Switch style={{ display: 'flex', alignItems: 'center', height: '20px' }} />
                </Form.Item>

                <SelectionsList
                  existingSelections={state.optionForm.selections}
                  newSelections={state.newSelections}
                  onUpdateSelection={updateNewSelection}
                  onRemoveNewSelection={removeNewSelection}
                  onRemoveExistingSelection={removeExistingSelection}
                  onAddSelection={addNewSelection}
                />

                {loadingOption ? (
                  <Spin />
                ) : (
                  <Button htmlType="submit" size="default" type="primary" key="submit" style={{ marginTop: 16 }}>
                    Actualizar Opción
                  </Button>
                )}
              </Form>
            </AddUser>
          </BasicFormWrapper>
        </Modal>

        {/* Table */}
        <Cards headless>
          <Row gutter={15}>
            <Col md={24}>
              <TableWrapper className="table-options table-responsive">
                <Table dataSource={dataSource} columns={columns} pagination={{ pageSize: 10 }} />
              </TableWrapper>
            </Col>
          </Row>
        </Cards>
      </Main>
    </>
  );
};

OptionsTable.propTypes = {
  searchName: PropTypes.string,
};

export default OptionsTable;
